import React, { useEffect, useMemo, useRef } from 'react'
import { FormattedCurrency, FormattedHTMLMessage, useBreakpoints } from '@web/walter-ui-kit'
import s from './s.module.css'
import { lang } from '~pages/CardsPage/Cards/Transactions/TransactionModal/lang'
import CopyText from '~components/CopyText'
import { formatDate as formatDateUtil } from '~utils/date'
import { Transaction } from '~pages/CardsPage/Cards/Transactions/types'
import { useGetThreeDSChallengesData, useCards } from '~api'
import { useCountdown } from '~hooks/useCountdown'
import CardImage from '~pages/CardsPage/components/CardImage'

// eslint-disable-next-line max-lines-per-function
export const useRows = (data?: Transaction, isChallenge?: boolean) => {
    const { isMobile } = useBreakpoints()
    const { data: cards } = useCards()

    const card = useMemo(() => {
        return cards?.cards?.find(cardData => cardData?.id === data?.card_id)
    }, [cards?.cards, data?.card_id])

    return useMemo(
        // eslint-disable-next-line complexity,max-lines-per-function
        () => [
            {
                title: <FormattedHTMLMessage {...lang.card} />,
                text:
                    isChallenge && card?.masked_number ? (
                        <div className={s.card}>
                            <div className={s.cardImg}>
                                <CardImage designId={card?.card_design?.id} />
                            </div>
                            <CopyText
                                type="element"
                                content={<span>·· {card?.masked_number || '****'}</span>}
                                copyText={card?.masked_number}
                                dataPointer="created_at"
                            />
                        </div>
                    ) : undefined,
            },
            {
                title: <FormattedHTMLMessage {...lang.createdAt} />,
                text: data?.created_at ? (
                    <CopyText
                        type="text"
                        copyText={formatDateUtil({ date: data?.created_at, formatString: 'dd MMMM yyyy, HH:mm' })}
                        dataPointer="created_at"
                    />
                ) : undefined,
            },
            {
                title: <FormattedHTMLMessage {...lang.updatedAt} />,
                text: data?.processed_at ? (
                    <CopyText
                        type="text"
                        copyText={formatDateUtil({ date: data?.processed_at, formatString: 'dd MMMM yyyy, HH:mm' })}
                        dataPointer="updated_at"
                    />
                ) : undefined,
            },
            {
                title: <FormattedHTMLMessage {...lang.transactionID} />,
                text:
                    !isChallenge && data?.id ? (
                        <CopyText
                            clampText
                            leftLength={isMobile ? 14 : 16}
                            rightLength={6}
                            type="text"
                            copyText={data?.id}
                            dataPointer="id"
                        />
                    ) : undefined,
            },
            {
                title: <FormattedHTMLMessage {...lang.amount} />,
                text: data?.amount ? (
                    <CopyText
                        type="element"
                        content={
                            <FormattedCurrency
                                full
                                value={data?.amount}
                                dataPointer="amount_value"
                                currency={data?.currency}
                                minimumFractionDigits={2}
                                maxPrecision={2}
                            />
                        }
                        copyText={data?.amount}
                        dataPointer="amount"
                    />
                ) : undefined,
            },
            {
                title: <FormattedHTMLMessage {...lang.merchant} />,
                text: data?.merchant_data?.name ? (
                    <CopyText
                        type="text"
                        clampText
                        leftLength={isMobile ? 14 : 16}
                        rightLength={6}
                        copyText={data?.merchant_data?.name}
                        dataPointer="merchant"
                    />
                ) : undefined,
            },
            {
                title: <FormattedHTMLMessage {...lang.merchantType} />,
                text: data?.merchant_data?.mcc_category ? (
                    <CopyText
                        type="text"
                        clampText
                        leftLength={isMobile ? 14 : 16}
                        rightLength={6}
                        copyText={data?.merchant_data?.mcc_category}
                        dataPointer="merchant_type"
                    />
                ) : undefined,
            },
            {
                title: <FormattedHTMLMessage {...lang.status} />,
                text:
                    data?.status && !isChallenge ? (
                        <CopyText type="text" copyText={data?.status} dataPointer="status" />
                    ) : undefined,
            },
        ],
        [isChallenge, card, data, isMobile]
    )
}

export const useChallenge = (data?: Transaction) => {
    const { data: chs } = useGetThreeDSChallengesData()
    const challenges = chs?.challenges
    const isChallengeRef = useRef(false)

    const challenge = useMemo(() => {
        return challenges?.find(ch => ch?.id === data?.id)
    }, [challenges, data?.id])

    useEffect(() => {
        if (challenge) {
            isChallengeRef.current = true
        }
    }, [challenge])

    const initialSeconds = useMemo(() => {
        if (!challenge?.expires_at) {
            return 0
        }

        const expiresMs = new Date(challenge?.expires_at).getTime()
        const nowMs = new Date().getTime()

        return Math.ceil((expiresMs - nowMs) / 1000)
    }, [challenge?.expires_at])

    const { seconds, isCountdownActive } = useCountdown(initialSeconds)

    const minutes = Math.floor(seconds / 60)
    const sec = Math.floor(seconds % 60)
    const timeString = minutes.toString().padStart(2, '0') + ':' + sec.toString().padStart(2, '0')

    return { timeString, isCountdownActive, isChallenge: isChallengeRef.current || !!challenge }
}
